import _ from 'lodash';
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { Trash } from '@phosphor-icons/react';
import { useRef, useState, useEffect, ReactNode } from 'react';
import { Label } from './Label';
import { cn } from 'src/utils/className';

export type Option = {
  id?: string;
  value: string;
  label?: string;
};

type AutoCompleteInputProps = {
  id: string;
  label?: ReactNode;
  placeholder?: string;
  value: string;
  fetchOptions: (query: string) => Promise<Option[]>;
  defaultValue?: Option;
  variant?: 'outlined' | 'contained' | 'text';
  required?: boolean;
  color?: 'primary' | 'secondary' | 'default';
  size?: 'small' | 'medium' | 'large';
  className?: string;
  labelClassName?: string;
  onChange?: (value: string) => void;
  onRemove?: (value: string) => Promise<void>;
  disabled?: boolean;
  direction?: 'up' | 'down';
  defaultOptions?: Option[];
};

const variantClasses = {
  contained: {
    primary: 'bg-primary text-neutral-0 hover:opacity-90',
    secondary: 'bg-neutral-400 text-neutral-0 hover:bg-neutral-500',
    default: 'bg-neutral-400 text-neutral-0 hover:bg-neutral-500',
  },
  outlined: {
    primary: 'border hover:border-primary hover:text-primary',
    secondary:
      'border border-neutral-400 text-neutral-400 hover:bg-neutral-400 hover:text-neutral-0',
    default:
      'border border-neutral-400 text-neutral-400 hover:bg-neutral-400 hover:text-neutral-0',
  },
  text: {
    primary: 'text-primary hover:bg-primary hover:text-neutral-0',
    secondary: 'text-neutral-400 hover:bg-neutral-400 hover:text-neutral-0',
    default: 'text-neutral-400 hover:bg-neutral-400 hover:text-neutral-0',
  },
};

const sizeClasses = {
  small: 'px-2 py-1 text-sm leading-6',
  medium: 'px-3 py-2.5 text-base leading-6',
  large: 'px-4 py-3 text-lg leading-7',
};

export const AutoCompleteInput = ({
  id,
  label,
  value,
  placeholder,
  fetchOptions,
  defaultValue,
  variant = 'outlined',
  color = 'primary',
  size = 'medium',
  className = '',
  labelClassName,
  onChange,
  onRemove,
  required,
  disabled = false,
  defaultOptions = [],
}: AutoCompleteInputProps) => {
  const [options, setOptions] = useState<Option[]>(defaultOptions);

  const debouncedFetchOptions = useRef(
    _.debounce(async (newQuery) => {
      try {
        const result = await fetchOptions(newQuery);

        setOptions(() =>
          newQuery === ''
            ? _.uniqBy(
                [...(defaultValue ? [defaultValue] : []), ...result],
                'value',
              )
            : result,
        );
      } catch (error) {
        console.error('Error fetching options:', error);
      }
    }, 300),
  );

  useEffect(() => {
    debouncedFetchOptions.current(value);
  }, [value]);

  const variantClass = variantClasses[variant][color];
  const sizeClass = sizeClasses[size];

  const handleRemove = async (option_id = '') => {
    await onRemove?.(option_id);
    debouncedFetchOptions.current(value);
  };

  return (
    <Combobox
      key={id}
      value={value}
      disabled={disabled}
      onChange={(value) => onChange?.(value || '')}
      immediate
    >
      <div className="relative">
        {label && (
          <Label
            htmlFor={id}
            className={cn('text-neutral-600', labelClassName)}
            required={required}
          >
            {label}
          </Label>
        )}
        <ComboboxInput
          className={cn(
            'w-full rounded-md border border-neutral-200',
            `${variantClass} ${sizeClass}`,
            'flex justify-between items-center leading-6',
            'focus:outline-none',
            className,
            disabled && 'opacity-50 cursor-not-allowed',
            'leading-[1.7] placeholder:text-[14.9px]',
          )}
          aria-label={id}
          placeholder={placeholder}
          required={required}
          onChange={(event) => onChange?.(event.target.value)}
        />

        <ComboboxOptions
          anchor="bottom"
          transition
          className={cn(
            'absolute z-50 mt-2 p-2 w-[var(--input-width)] rounded border border-neutral-100 bg-neutral-0 [--anchor-gap:var(--spacing-1)] empty:invisible',
            'transition duration-100 ease-in absolute',
          )}
        >
          {options.map((option) => (
            <div className="relative" key={option.value}>
              {!!onRemove && (
                <Trash
                  className="absolute z-[60] right-2 top-1/2 -translate-y-1/2 cursor-pointer text-error"
                  onClick={() => handleRemove(option.id)}
                />
              )}
              <ComboboxOption
                value={option.value}
                className="select-none relative py-1 px-2 rounded-lg text-neutral-600 hover:bg-neutral-50 flex items-center justify-between"
              >
                <p className="text-sm/6">{option.label}</p>
              </ComboboxOption>
            </div>
          ))}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
};
